import { useCallback } from 'react';

export type Primitive = string | number | boolean;

export type FilterConfig<T> =
  | {
      type: 'field';
      field: keyof T;
      value?: Primitive | Primitive[];
    }
  | {
      type: 'array-object';
      field: keyof T;
      matchOn: string;
      values?: Primitive[];
    };

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export function useOfflineFilter<T>() {
  const getFilteredResults = useCallback(
    (data: T[], filters?: FilterConfig<T>[]): T[] => {
      if (!filters || filters.length === 0) return data;

      const activeFilters = filters.filter((filter) => {
        if (filter.type === 'field') {
          return hasValue(filter.value);
        }
        if (filter.type === 'array-object') {
          return hasValue(filter.values);
        }
        return false;
      });

      // ✅ No active filters → return full data
      if (activeFilters.length === 0) return data;

      return data.filter((item) =>
        activeFilters.every((filter) => {
          // 🔹 Simple field filter
          if (filter.type === 'field') {
            const value = (item as any)[filter.field];

            if (Array.isArray(filter.value)) {
              return filter.value.includes(value);
            }

            return value === filter.value;
          }

          // 🔹 Array of objects filter
          if (filter.type === 'array-object') {
            const array = (item as any)[filter.field];

            if (!Array.isArray(array)) return false;

            return array.some((obj) =>
              filter.values!.includes(obj?.[filter.matchOn])
            );
          }

          return true;
        })
      );
    },
    []
  );

  return { getFilteredResults };
}
